import {
  AgendaSpeaker,
  PublicEventDetailsResponse,
} from '@hubs101/booking-api-skd-client/lib/types';
import { Image, Text, View, Link, Page } from '@react-pdf/renderer';
import { getImageSrc } from '@utils/fn';

import { useTranslation } from 'react-i18next';
import { globalStyles } from '../../styles';
import { styles } from './styles';
import { SafeImage } from '../../components/SafeImage';
import { truncateText, normalizeText } from '../../../utils';
import { SPEAKER_EMAIL } from '@utils/constants';

const clampToTwoLines = (text: string, maxCharsPerLine = 22): string => {
  if (!text) return '';

  const words = text.split(' ');
  let line1 = '';
  let line2 = '';
  let currentLine = 1;

  for (const word of words) {
    if (currentLine === 1) {
      if ((line1 + ' ' + word).trim().length <= maxCharsPerLine) {
        line1 = (line1 + ' ' + word).trim();
      } else {
        currentLine = 2;
        line2 = word;
      }
    } else {
      if ((line2 + ' ' + word).trim().length <= maxCharsPerLine) {
        line2 = (line2 + ' ' + word).trim();
      } else {
        return `${line1}\n${line2}...`;
      }
    }
  }

  return line2 ? `${line1}\n${line2}` : line1;
};

interface SpeakersAndModeratorsPageProps {
  event: PublicEventDetailsResponse;
  agendaSpeakers: AgendaSpeaker[];
  header: JSX.Element;
  footer: JSX.Element;
}

const SpeakersAndModeratorsPage = ({
  event,
  agendaSpeakers,
  header,
  footer,
}: SpeakersAndModeratorsPageProps) => {
  const { t } = useTranslation();

  const ITEMS_PER_ROW = 6;
  const speakers =
    agendaSpeakers?.filter(
      speaker =>
        speaker?.first_name &&
        speaker?.last_name &&
        speaker?.job?.title &&
        speaker?.images?.picture &&
        speaker?.company?.logo
    ) || [];

  const text = t('become-a-speaker-text') ?? '';
  const questionIndex = text.indexOf('?');
  const firstSentence =
    questionIndex !== -1 ? text.slice(0, questionIndex + 1) : text;
  const rest = questionIndex !== -1 ? text.slice(questionIndex + 1).trim() : '';

  const chunkedSpeakers = [];
  for (let i = 0; i < speakers.length; i += ITEMS_PER_ROW) {
    chunkedSpeakers.push(speakers.slice(i, i + ITEMS_PER_ROW));
  }

  const getImageHeight = () => {
    const speakersCount = speakers?.length ?? 0;

    if (speakersCount === 0) return 250;
    else if (speakersCount > 0 && speakersCount <= 12) return 250;
    else if (speakersCount > 12 && speakersCount <= 18) return 110;
    return 250;
  };

  const speakerSubject = t('apply-as-a-speaker-request-subject', {
    eventName: event?.name,
  });
  const speakerBody = t('apply-as-a-speaker-request-body', {
    eventName: event?.name,
  });
  const speakerEmailHref = `mailto:${SPEAKER_EMAIL}?subject=${encodeURIComponent(speakerSubject)}&body=${encodeURIComponent(speakerBody)}`;

  return (
    <Page style={styles.page}>
      {header}

      <View style={styles.contentWrapper}>
        <Text
          style={{
            ...styles.title,
            color: event?.design?.themeColor,
          }}
        >
          {t('speakers-&-moderators')}{' '}
          {new Date(event.start_date).getFullYear()}
        </Text>

        <View
          style={{
            ...globalStyles.lineBar,
            borderColor: event?.design?.themeColor,
            marginLeft: 0,
            marginBottom: 10,
            width: 89,
          }}
        />

        <View
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: speakers?.length === 0 ? 'center' : 'space-between',
          }}
        >
          <View
            style={{
              ...styles.moderatorsWrapper,
              minHeight: speakers?.length === 0 ? 100 : 270,
            }}
          >
            {chunkedSpeakers.map((row, rowIndex) => (
              <View key={rowIndex} style={styles.rowWrapper}>
                {row.map(speaker => {
                  return (
                    <View
                      style={styles.moderatorItemWrapper}
                      key={speaker.profile}
                      wrap={false}
                    >
                      <View style={styles.speakerImagesWrapper}>
                        {speaker.images?.picture?.length > 0 && (
                          <SafeImage
                            style={styles.speakerImage}
                            src={getImageSrc(speaker.images.picture)}
                            debugLabel={`speaker-picture-image-${speaker.profile}`}
                          />
                        )}
                      </View>

                      <View style={styles.companyImagesWrapper}>
                        {speaker.company?.logo?.length > 0 && (
                          <SafeImage
                            style={styles.companyLogo}
                            src={getImageSrc(speaker.company.logo)}
                            debugLabel={`moderator-company-logo-${speaker.profile}`}
                          />
                        )}
                      </View>

                      <View
                        style={{
                          ...styles.moderatorDetailsWrapper,
                          borderColor: event?.design?.themeColor,
                        }}
                      >
                        <Text style={styles.speakerName}>
                          {truncateText(normalizeText(speaker.first_name), 20)}
                        </Text>
                        <Text style={styles.speakerName}>
                          {truncateText(normalizeText(speaker.last_name), 20)}
                        </Text>
                        <Text style={styles.speakerCompanyName}>
                          {clampToTwoLines(
                            truncateText(
                              normalizeText(speaker?.job?.title),
                              50
                            ),
                            22
                          )}
                        </Text>
                      </View>
                    </View>
                  );
                })}
              </View>
            ))}
          </View>

          <View
            style={{
              ...styles.speakingSection,
              marginTop: speakers?.length > 18 ? 180 : 40,
            }}
            wrap={false}
          >
            <Text style={styles.speakingTitle}>{firstSentence}</Text>

            <View
              style={{
                ...styles.speakingLine,
                backgroundColor: event?.design?.themeColor,
              }}
            />

            <Text style={styles.speakingDescription}>{rest}</Text>

            <View
              style={{
                ...styles.speakingButton,
                backgroundColor: event?.design?.themeColor,
              }}
            >
              <Text style={styles.speakingButtonWrapper}>
                <Link
                  href={speakerEmailHref}
                  style={{ textDecoration: 'none', color: '#ffffff' }}
                >
                  {t('become-a-speaker-button')}
                </Link>
              </Text>
            </View>

            {/* 4 Image Columns */}
            <View style={styles.speakingImagesRow}>
              <Image
                key={`image-1`}
                src={`https://media.we-conect.com/website/portfolios/${event?.portfolio?.slug}/event_guide_speakers_1.png`}
                style={{
                  ...styles.speakingImage,
                  borderTopLeftRadius: 4,
                  borderBottomLeftRadius: 4,
                  height: getImageHeight(),
                }}
              />
              <Image
                key={`image-2`}
                src={`https://media.we-conect.com/website/portfolios/${event?.portfolio?.slug}/event_guide_speakers_2.png`}
                style={{ ...styles.speakingImage, height: getImageHeight() }}
              />
              <Image
                key={`image-3`}
                src={`https://media.we-conect.com/website/portfolios/${event?.portfolio?.slug}/event_guide_speakers_3.png`}
                style={{ ...styles.speakingImage, height: getImageHeight() }}
              />
              <Image
                key={`image-4`}
                src={`https://media.we-conect.com/website/portfolios/${event?.portfolio?.slug}/event_guide_speakers_4.png`}
                style={{
                  ...styles.speakingImage,
                  borderTopRightRadius: 4,
                  borderBottomRightRadius: 4,
                  height: getImageHeight(),
                }}
              />
            </View>
          </View>
        </View>
      </View>

      {footer}
    </Page>
  );
};

export default SpeakersAndModeratorsPage;
